"use client";

import { useState } from "react";

import { CircleChevronRight, Info, RotateCcw, TriangleAlert } from "lucide-react";

import { cn } from "@repo/ui/lib/utils";

import type { WalletReserveToWithdrawModalDTO } from "../../types/cms/profile-page";
import type { WalletAsyncStatus, WalletWithdrawableReservation } from "../../types/wallet";
import { getCreditExpiryColorClass } from "../../types/wallet";
import { formatReservationExpiryDate, formatWalletAmount } from "../../utils/walletFormatters";
import { FlightSpinLoader } from "../common/Loader";
import { CommonDialog } from "../common/modal/CommonDialog";
import { JetSmartRadioGroup, JetSmartRadioGroupItem } from "../JetSmartRadioGroup";
import { Button } from "../ui/button";

interface WalletReserveToWithdrawModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableBalance: number;
  currency: string;
  reservations: WalletWithdrawableReservation[];
  status?: WalletAsyncStatus;
  cms: WalletReserveToWithdrawModalDTO;
  onNext: (reservation: WalletWithdrawableReservation) => void | Promise<void>;
  isSubmitting?: boolean;
  submitError?: string | null;
}

export function WalletReserveToWithdrawModal({
  open,
  onOpenChange,
  availableBalance,
  currency,
  reservations,
  status = "success",
  cms,
  onNext,
  isSubmitting = false,
  submitError = null,
}: Readonly<WalletReserveToWithdrawModalProps>) {
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const [genericError, setGenericError] = useState<string | null>(null);
  const selectedReservation = reservations.find((r) => r.id === selectedId);
  const isLoading = status === "idle" || status === "loading";
  const isListError = status === "error";

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setSelectedId(undefined);
      setGenericError(null);
    }
    onOpenChange(nextOpen);
  };

  const handleNext = () => {
    if (!selectedReservation) return;
    setGenericError(null);
    Promise.resolve(onNext(selectedReservation)).catch(() => {
      setGenericError(cms.genericErrorMessage);
    });
  };

  return (
    <CommonDialog
      open={open}
      onOpenChange={handleOpenChange}
      testId="wallet-reserve-to-withdraw-modal"
      closeOnOverlayClick={false}
      closeOnEsc={false}
      overlayClassName="z-[9999]"
      contentClassName="z-[9999] w-[800px] max-w-[calc(100%-2rem)] sm:max-w-[800px] max-h-[95vh] p-0 gap-0 flex flex-col overflow-hidden rounded-[12px] border-none bg-white shadow-[0px_4px_8px_rgba(0,0,0,0.1)] max-sm:w-full max-sm:max-w-full max-sm:max-h-[85vh] max-sm:top-auto max-sm:left-0 max-sm:right-0 max-sm:bottom-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-b-none"
      closeButtonClassName="bg-transparent text-brand-black-50 hover:text-brand-black-30 top-[13px] right-3.5 sm:right-10"
      headerClassName="px-6 sm:px-12 pt-6 sm:pt-12 pb-0 text-left"
      title={
        <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-2 pr-6 max-sm:flex-col max-sm:items-start max-sm:gap-3">
          <div className="flex items-center gap-2">
            {cms.iconTitleUrl ? (
              <img src={cms.iconTitleUrl} alt="" className="h-5 w-6 shrink-0" aria-hidden="true" />
            ) : (
              <RotateCcw className="h-5 w-6 text-brand-cyan shrink-0" aria-hidden="true" />
            )}
            <span className="text-xl leading-[25px] tracking-[-0.02em] font-bold text-brand-blue">
              {cms.heading}
            </span>
          </div>
          <div className="flex max-sm:w-full items-center gap-[5px]">
            <span className="min-w-0 flex-1 text-sm leading-[18px] font-normal text-brand-black-30 sm:flex-none sm:text-base sm:leading-5">
              {cms.availableBalanceLabel}
            </span>
            <span
              className="flex shrink-0 items-center justify-center whitespace-nowrap rounded-full bg-brand-cyan/10 px-1.5 pt-1 pb-[5px] text-sm leading-[18px] font-[700] text-brand-cyan sm:px-2.5 sm:text-base sm:leading-5"
              data-testid="reserve-to-withdraw-available-balance"
            >
              {formatWalletAmount(availableBalance)} {currency}
            </span>
          </div>
        </div>
      }
    >
      <div className="px-6 sm:px-12 pt-6">
        <div className="flex items-center gap-2.5 rounded-md bg-[#E4EAF1] px-2.5 py-2">
          {cms.infoIconUrl ? (
            <img src={cms.infoIconUrl} alt="" className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          ) : (
            <Info className="h-3.5 w-3.5 shrink-0 text-brand-blue" aria-hidden="true" />
          )}
          <p className="text-xs leading-[14px] font-[700] text-brand-blue">
            {cms.singleReturnNoticeMessage}
          </p>
        </div>
      </div>

      <p className="px-6 sm:px-12 pt-6 pb-0 text-xs leading-[14px] font-[700] text-brand-blue">
        {cms.selectSingleReservationLabel}
      </p>

      <div
        className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-6 sm:px-12 pt-2.5 pb-1 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-brand-black-70 [&::-webkit-scrollbar-track]:bg-transparent"
        style={{ scrollbarWidth: "thin", scrollbarColor: "var(--brand-black-70) transparent" }}
        data-testid="reserve-to-withdraw-list"
      >
        {isLoading && (
          <div className="flex items-center justify-center py-10">
            <FlightSpinLoader />
          </div>
        )}

        {isListError && (
          <div
            className="flex items-center gap-2.5 py-6 text-sm text-red-500"
            data-testid="reserve-to-withdraw-list-error"
          >
            <TriangleAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
            <p>{cms.errorMessage}</p>
          </div>
        )}

        {!isLoading && !isListError && reservations.length === 0 && (
          <p className="py-6 text-sm text-brand-black-50" data-testid="reserve-to-withdraw-empty">
            {cms.emptyStateMessage}
          </p>
        )}

        {!isLoading && !isListError && reservations.length > 0 && (
          <JetSmartRadioGroup
            value={selectedId}
            onValueChange={setSelectedId}
            className="min-w-0 gap-2.5"
          >
            {reservations.map((reservation) => {
              const isSelected = reservation.id === selectedId;
              return (
                <label
                  key={reservation.id}
                  htmlFor={`reservation-${reservation.id}`}
                  className={cn(
                    "flex min-h-[86px] w-full min-w-0 shrink-0 cursor-pointer items-center gap-3 rounded-md border bg-white px-3 py-3 transition-colors duration-150 sm:h-16 sm:min-h-0 sm:py-0",
                    isSelected
                      ? "border-brand-cyan"
                      : "border-brand-black-70 hover:border-brand-black-60",
                  )}
                  data-testid="reserve-to-withdraw-item"
                >
                  <JetSmartRadioGroupItem
                    value={reservation.id}
                    id={`reservation-${reservation.id}`}
                    checked={isSelected}
                    radioGroupDot="brand-cyan"
                    radioButtonDotSize="3"
                    className={cn(
                      "flex h-5 w-5 shrink-0 items-center justify-center border-[1.76px] bg-white",
                      isSelected ? "border-brand-cyan" : "border-brand-black-60",
                    )}
                  />

                  <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
                    <div className="flex min-w-0 flex-col gap-1">
                      <p className="truncate text-sm leading-[18px] text-brand-blue">
                        {cms.returnLabelPrefix} {reservation.bookingCode}
                      </p>
                      {reservation.flightInfo && (
                        <p className="text-xs leading-[14px] text-brand-black-50 sm:truncate sm:text-sm sm:leading-[18px]">
                          {reservation.flightInfo}
                        </p>
                      )}
                    </div>

                    <div className="flex w-[58px] shrink-0 flex-col items-end gap-0.5 sm:w-auto sm:gap-[3px]">
                      <p className="text-sm leading-[18px] font-medium text-brand-blue">
                        ${formatWalletAmount(reservation.amount)}
                      </p>
                      <p
                        className={cn(
                          "w-full text-xs leading-[14px] font-medium text-right sm:w-auto sm:whitespace-nowrap",
                          getCreditExpiryColorClass("ACTIVE", reservation.isExpiringSoon),
                        )}
                      >
                        {cms.expiresOnLabel} {formatReservationExpiryDate(reservation.expireAt)}
                      </p>
                    </div>
                  </div>
                </label>
              );
            })}
          </JetSmartRadioGroup>
        )}
      </div>

      <div className="flex flex-col items-end gap-2 px-6 sm:px-12 pb-6 sm:pb-12 pt-6">
        {submitError && (
          <p className="text-xs text-red-500" data-testid="reserve-to-withdraw-submit-error">
            {cms.redirectErrorMessage}
          </p>
        )}
        {!submitError && genericError && (
          <p className="text-xs text-red-500" data-testid="reserve-to-withdraw-generic-error">
            {genericError}
          </p>
        )}
        <Button
          type="button"
          className="h-[42px] w-full shrink-0 rounded-full bg-brand-blue px-5 text-lg font-medium text-white hover:bg-brand-cyan disabled:bg-brand-black-50 disabled:opacity-100 disabled:hover:bg-brand-black-50 sm:w-[138px]"
          disabled={!selectedReservation || isSubmitting}
          onClick={handleNext}
          data-testid="reserve-to-withdraw-next-button"
        >
          {cms.nextButtonLabel}
          <CircleChevronRight className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    </CommonDialog>
  );
}

export default WalletReserveToWithdrawModal;
